import { type FC, useEffect, useState, useMemo, Suspense } from "react";
import {
    miniApp,
    themeParams,
    useSignal,
    hapticFeedback,
    viewport,
    initData,
    settingsButton,
    useLaunchParams,
    swipeBehavior,
    openTelegramLink,
} from "@telegram-apps/sdk-react";
import { RouterProvider } from "react-router";
import { Provider } from "react-redux";
import * as Sentry from "@sentry/react";
import { AppRoot } from "@telegram-apps/telegram-ui";

import i18nextInstance from "~/i18n/index";
import { SystemError } from "~/components/SystemError";
import InitLoading from "~/components/InitLoading";
import { SnackbarProvider } from "~/Provider";

import { createRouter } from "~/navigation/routes.tsx";
import { initStoreCommon } from "~/store";

import { changeTheme } from "~/utils/common";
import UXUYWalletProvider from "~/Provider/UXUYWallet";
import { setTheme } from "~/utils/themeHepler";
import { tgLink, tgLinkType } from "~/utils/tgLink";

import initialStore from "~/store";
import useAppNewUser from "~/hook/useAppNewUser";
import { setupUserProperties, xyLogEvent } from "~/hook/useFirebase";
import { resetMainButton } from "~/hook/useMainButton";
import { config } from "~/config";


function replaceCode(str: string, tgId: number) {
    // 先检查是否存在 code
    const hasCode = /^A_[^_]+/.test(str);

    // 只有在有 code 的情况下才替换
    if (hasCode) {
        // 保持相同的替换逻辑
        return str.replace(/^A_([^_]*)(_.*)?$/, `A_${tgId}$2`);
    }

    // 如果没有 code，返回原字符串
    return str;
}

export const App: FC = () => {
    const lp = useLaunchParams();
    const initDataState = useSignal(initData.state);
    const isDark = useSignal(themeParams.isDark);

    const isNewUser = useAppNewUser();
    const router = useMemo(() => createRouter(isNewUser), [isNewUser]);
    const store = useMemo(() => initialStore({}), []);

    const [isAppLoading, setIsAppLoading] = useState(true);

    async function init() {
        const userLanguageCode = initDataState?.user?.languageCode;
        if (userLanguageCode) {
            i18nextInstance.changeLanguage(userLanguageCode);
        }
        const startParam = decodeURIComponent(initDataState?.startParam || "");
        const paramsLink = tgLink.parseLink(startParam) || ({} as any);

        // jump to external link
        if (
            paramsLink?.type === tgLinkType.D &&
            paramsLink?.params?.url &&
            paramsLink?.params?.url?.length > 0
        ) {
            globalThis.location.replace(paramsLink.params.url);
            return;
        }

        if (paramsLink?.params?.referral_group_id) {
            const groupId = paramsLink?.params?.referral_group_id;
            window.sessionStorage.setItem(
                "__uxuy_dex_cached_referral_group_id__",
                groupId
            );
        }

        await initStoreCommon(store);

        miniApp.ready();
        hapticFeedback.impactOccurred("heavy");
        setupUserProperties({
            user_id: initDataState?.user?.id,
            user_name: initDataState?.user?.username,
        });
        xyLogEvent("TG DEX Initialized");
        resetMainButton();

        if (swipeBehavior.isSupported()) {
            swipeBehavior.disableVertical();
        }

        if (["android", "ios"].includes(lp.platform)) {
            viewport.expand();
            // if (viewport.requestFullscreen.ifAvailable()) {
            //     viewport.requestFullscreen();
            // }
        }

        settingsButton.show();
        settingsButton.onClick(() => {
            router.navigate("/setting");
        });

        setIsAppLoading(false);

        // jump to inner link
        if (paramsLink && paramsLink?.path) {
            if (startParam.startsWith('A_')) {
                const tgUserId = initDataState?.user?.id;
                if (tgUserId) {
                    openTelegramLink(`${config.wallet_direct_link}?startapp=${replaceCode(startParam, tgUserId)}`)
                } else {
                    openTelegramLink(`${config.wallet_direct_link}?startapp=${startParam}`)
                }
            } else if (startParam.startsWith('B_')) {
                openTelegramLink(`${config.wallet_direct_link}?startapp=${startParam.replace(/^B_/, "T_")}`)
            } else {
                openTelegramLink(`${config.wallet_direct_link}?startapp=${startParam}`)
            }
        }
    }

    useMemo(
        () =>
            init().catch((err) => {
                throw err;
            }),
        []
    );

    useEffect(() => {
        const theme = isDark ? "dark" : "light";
        changeTheme(theme);
        setTheme(theme);
    }, [isDark]);

    return (
        <AppRoot appearance={isDark ? "dark" : "light"} platform={"ios"}>
            {isAppLoading ? (
                <InitLoading />
            ) : (
                <Suspense fallback={<InitLoading />}>
                    <Sentry.ErrorBoundary
                        fallback={SystemError as Sentry.FallbackRender}
                    >
                        <Provider store={store}>
                            <UXUYWalletProvider>
                                <SnackbarProvider>
                                    <RouterProvider router={router} />
                                </SnackbarProvider>
                            </UXUYWalletProvider>
                        </Provider>
                    </Sentry.ErrorBoundary>
                </Suspense>
            )}
        </AppRoot>
    );
};
